import type { Cycle, Phase } from "../hooks/useCycles";
import type { EmbryoFreezingPlan } from "@src/packages/embryo-freezing-plans/hooks/useEmbryoFreezingPlans";

export type PgtPlan = EmbryoFreezingPlan["versions"][number]["pgtPlan"];
type PgtRecordPresence = Pick<
  NonNullable<Phase["testResults"]>[number],
  "name"
>;
export type ConditionalCyclePhase = Pick<Phase, "type" | "triggerShot"> & {
  testResults?: PgtRecordPresence[];
};

export type ConditionalCycleGraph = {
  omitProtocol: boolean;
  includeInsemination: boolean;
  hasTriggerData: boolean;
  includePgt: boolean;
};

type CyclePlanSnapshot = NonNullable<NonNullable<Cycle["cyclePlanSnapshot"]>>;
type CyclePlanGraphStep = CyclePlanSnapshot["graph"]["steps"][number];

export type CyclePlanFlowStep = {
  id:
    | "pre_treatment"
    | "protocol"
    | "sperm_collection"
    | "insemination"
    | "luteal_phase";
  applicability: CyclePlanGraphStep["applicability"];
};

export type CyclePlanFlow = {
  steps: CyclePlanFlowStep[];
  hasTriggerOrSurge: boolean;
};

const PLAN_STEP_TO_FLOW_STEP: Partial<
  Record<CyclePlanGraphStep["kind"], CyclePlanFlowStep["id"]>
> = {
  baseline: "pre_treatment",
  medication: "protocol",
  sperm_collection: "sperm_collection",
  insemination: "insemination",
  luteal_support: "luteal_phase",
  wait_test: "luteal_phase",
};

/**
 * Reduce the server-owned IUI/OI graph to the patient flow steps that already
 * have canonical components. Monitoring, clinician decisions, trigger/surge,
 * and specimen preparation remain represented by the server graph but are not
 * invented as new patient UI until those product surfaces exist.
 */
export function resolveCyclePlanFlow(
  snapshot: Cycle["cyclePlanSnapshot"] | null | undefined,
): CyclePlanFlow | null {
  if (!snapshot || snapshot.status !== "active") return null;

  const byId = new Map<CyclePlanFlowStep["id"], CyclePlanFlowStep>();
  let hasTriggerOrSurge = false;

  for (const planStep of snapshot.graph.steps) {
    if (planStep.kind === "trigger_or_surge") {
      hasTriggerOrSurge = true;
    }
    if (planStep.applicability === "not_applicable") continue;

    const id = PLAN_STEP_TO_FLOW_STEP[planStep.kind];
    if (!id) continue;

    const existing = byId.get(id);
    if (!existing || planStep.applicability === "required") {
      byId.set(id, {
        id,
        applicability: planStep.applicability,
      });
    }
  }

  const steps = snapshot.graph.steps.flatMap((planStep) => {
    const id = PLAN_STEP_TO_FLOW_STEP[planStep.kind];
    if (!id || planStep.applicability === "not_applicable") return [];
    const step = byId.get(id);
    if (!step || step.id !== id) return [];
    byId.delete(id);
    return [step];
  });

  return steps.length > 0 ? { steps, hasTriggerOrSurge } : null;
}

/**
 * Derive only the conditional facts that the current API can prove.
 *
 * The accepted cycle-plan contract will eventually replace this adapter. Until
 * then, an IUI protocol and returned phase records are the available sources;
 * absent records remain unknown rather than becoming inferred care.
 */
export function resolveConditionalCycleGraph(
  cycleType: Cycle["cycleType"],
  protocol: Cycle["protocol"],
  phases: ConditionalCyclePhase[],
  pgtPlan?: PgtPlan | null,
): ConditionalCycleGraph {
  const hasPersistedPgtRecord = phases.some((phase) =>
    phase.testResults?.some((result) => result.name?.toLowerCase() === "pgt"),
  );

  return {
    omitProtocol:
      cycleType === "iui" &&
      (protocol === "natural" || protocol === "modified_natural"),
    includeInsemination:
      cycleType === "oi" &&
      phases.some((phase) => phase.type === "insemination"),
    hasTriggerData: phases.some((phase) => phase.triggerShot != null),
    // A PGT step is only shown when the plan explicitly leaves room for it,
    // or when a previously saved PGT decision/result needs to remain visible.
    // An absent plan is not permission to imply that PGT applies.
    includePgt:
      (cycleType === "ivf" ||
        cycleType === "icsi" ||
        cycleType === "embryo_freezing") &&
      (hasPersistedPgtRecord || (pgtPlan != null && pgtPlan !== "none")),
  };
}
